import { useState } from 'react';
import {
  Wrench,
  Send,
  CheckCircle2,
  Clock,
  ShieldCheck,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';

interface WorkOrder {
  id: string;
  assetTag: string;
  cmmsSystem: 'SAP PM' | 'IBM Maximo';
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'dispatched' | 'acknowledged' | 'in_progress' | 'completed';
  type: 'repair' | 'inspection' | 'clamp_installation' | 'rerate_audit';
  description: string;
  externalId: string;
  dispatchedAt: string;
  workCenter: string;
  estimatedHours: number;
}

const INITIAL_WORK_ORDERS: WorkOrder[] = [
  {
    id: 'WO-2026-081',
    assetTag: '12"-HC-2019-CS',
    cmmsSystem: 'SAP PM',
    priority: 'high',
    status: 'dispatched',
    type: 'clamp_installation',
    description: 'Local metal loss (t_min 6.85mm) on 90° elbow. Install temporary engineered sleeve prior to turnaround.',
    externalId: 'SAP-WO-884912',
    dispatchedAt: 'Today, 10:14',
    workCenter: 'MECH-PIPE-04',
    estimatedHours: 24,
  },
  {
    id: 'WO-2026-079',
    assetTag: '06"-CDU-1044-A106B',
    cmmsSystem: 'IBM Maximo',
    priority: 'critical',
    status: 'acknowledged',
    type: 'repair',
    description: 'Severe pitting (4.12mm remaining) at support shoe low point. Full 6m spool replacement approved.',
    externalId: 'MAX-78210',
    dispatchedAt: 'Yesterday, 16:30',
    workCenter: 'FAB-CREW-1',
    estimatedHours: 48,
  },
  {
    id: 'WO-2026-074',
    assetTag: 'COLUMN-C101-FEED',
    cmmsSystem: 'SAP PM',
    priority: 'medium',
    status: 'in_progress',
    type: 'inspection',
    description: 'Thermal differential +8.4°C indicates wet CaSil pocket. Strip 1.5m weather jacket for PEC screening.',
    externalId: 'SAP-WO-884650',
    dispatchedAt: '2 days ago',
    workCenter: 'INSP-NDT-02',
    estimatedHours: 12,
  },
];

export function CMMSPage() {
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>(INITIAL_WORK_ORDERS);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // New Work Order Form State
  const [formData, setFormData] = useState({
    assetTag: '10"-VDU-3302-CS',
    cmmsSystem: 'SAP PM' as const,
    priority: 'high' as const,
    type: 'repair' as const,
    description: 'API 579-1 FFS assessment indicated RSF = 0.812 (below 0.900). Localized metal loss requires pressure rerate or external patch.',
    recommendedAction: 'Erect localized scaffolding, verify ultrasonic thickness grid, install composite repair sleeve.',
    workCenter: 'MECH-PIPE-02',
    estimatedHours: 16,
  });

  const handleDispatch = async () => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 800));
      const newOrder: WorkOrder = {
        id: `WO-2026-0${workOrders.length + 82}`,
        assetTag: formData.assetTag,
        cmmsSystem: formData.cmmsSystem,
        priority: formData.priority,
        status: 'dispatched',
        type: formData.type,
        description: formData.description,
        externalId: formData.cmmsSystem === 'SAP PM' ? `SAP-WO-${Math.floor(100000 + Math.random() * 900000)}` : `MAX-${Math.floor(10000 + Math.random() * 90000)}`,
        dispatchedAt: 'Just now',
        workCenter: formData.workCenter,
        estimatedHours: formData.estimatedHours,
      };

      setWorkOrders([newOrder, ...workOrders]);
      toast.success(`Successfully dispatched ${newOrder.externalId} to ${formData.cmmsSystem}`);
    } catch {
      toast.error('Failed to dispatch work order');
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'critical':
        return <span className="badge badge-danger">CRITICAL</span>;
      case 'high':
        return <span className="badge badge-warning">HIGH</span>;
      case 'medium':
        return <span className="badge badge-info">MEDIUM</span>;
      case 'low':
        return <span className="badge badge-success">LOW</span>;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'dispatched':
        return <span className="text-blue-600 font-mono text-xs flex items-center gap-1 font-semibold"><Clock className="h-3.5 w-3.5" /> Dispatched</span>;
      case 'acknowledged':
        return <span className="text-purple-600 font-mono text-xs flex items-center gap-1 font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Acknowledged</span>;
      case 'in_progress':
        return <span className="text-amber-600 font-mono text-xs flex items-center gap-1 font-semibold"><Clock className="h-3.5 w-3.5" /> In Progress</span>;
      case 'completed':
        return <span className="text-green-600 font-mono text-xs flex items-center gap-1 font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Completed</span>;
      default:
        return null;
    }
  };

  const filteredOrders = workOrders.filter((wo) =>
    wo.assetTag.toLowerCase().includes(searchQuery.toLowerCase()) ||
    wo.externalId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    wo.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="telemetry-pill bg-indigo-50 text-indigo-700 font-bold border border-indigo-200">
                ENTERPRISE WORK ORDER INTEGRATION
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2.5">
              <Wrench className="h-7 w-7 text-indigo-600" />
              CMMS Work Order Dispatch Console
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Bidirectional enterprise synchronization with SAP Plant Maintenance (PM) and IBM Maximo with evidence dossiers.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <span className="telemetry-pill bg-green-50 text-green-700 font-mono font-bold">
              SAP PM BAPI: CONNECTED
            </span>
            <span className="telemetry-pill bg-green-50 text-green-700 font-mono font-bold">
              MAXIMO REST: ONLINE
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Create Work Order Form */}
        <div className="lg:col-span-7 card bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="card-header border-b border-slate-100 p-5 flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900">Dispatch Maintenance Work Order</h2>
            <span className="telemetry-pill bg-slate-100 text-slate-600 font-mono">EVIDENCE DOSSIER ATTACHED</span>
          </div>
          <div className="card-body p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Target CMMS System</label>
                <select
                  value={formData.cmmsSystem}
                  onChange={(e) => setFormData({ ...formData, cmmsSystem: e.target.value as any })}
                  className="input font-mono font-bold"
                >
                  <option value="SAP PM">SAP Plant Maintenance (PM)</option>
                  <option value="IBM Maximo">IBM Maximo Asset Management</option>
                </select>
              </div>

              <div>
                <label className="label">Asset / Functional Location</label>
                <input
                  type="text"
                  className="input font-mono font-bold"
                  value={formData.assetTag}
                  onChange={(e) => setFormData({ ...formData, assetTag: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="label">Maintenance Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                  className="input"
                >
                  <option value="critical">Critical (Immediate 24h)</option>
                  <option value="high">High (&lt; 7 Days)</option>
                  <option value="medium">Medium (Turnaround)</option>
                  <option value="low">Low (Routine)</option>
                </select>
              </div>

              <div>
                <label className="label">Order Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  className="input"
                >
                  <option value="repair">Repair / Replacement</option>
                  <option value="clamp_installation">Clamp / Pressure Sleeve</option>
                  <option value="inspection">Targeted NDT Screening</option>
                  <option value="rerate_audit">Engineering Rerate Audit</option>
                </select>
              </div>

              <div>
                <label className="label">Assigned Work Center</label>
                <input
                  type="text"
                  className="input font-mono"
                  value={formData.workCenter}
                  onChange={(e) => setFormData({ ...formData, workCenter: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="label">Scope of Work & Defect Classification</label>
              <textarea
                rows={3}
                className="input font-sans text-xs sm:text-sm"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div>
              <label className="label">Engineering Remediation Recommendation</label>
              <textarea
                rows={2}
                className="input font-sans text-xs sm:text-sm"
                value={formData.recommendedAction}
                onChange={(e) => setFormData({ ...formData, recommendedAction: e.target.value })}
              />
            </div>

            <button
              onClick={handleDispatch}
              disabled={isLoading}
              className="btn btn-primary w-full py-3 text-sm font-semibold shadow-md mt-2 flex items-center justify-center gap-2"
            >
              <Send className="h-4 w-4" />
              <span>{isLoading ? 'Dispatching to Enterprise API...' : `Dispatch Work Order to ${formData.cmmsSystem}`}</span>
            </button>
          </div>
        </div>

        {/* Evidence Dossier Visual Attachment */}
        <div className="lg:col-span-5 space-y-4">
          <div className="card bg-slate-900 text-white rounded-xl overflow-hidden shadow-xl border border-slate-800 p-5 space-y-4 font-mono text-xs">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <ShieldCheck className="h-4 w-4 text-green-400" />
                <span>LINKED EVIDENCE DOSSIER</span>
              </div>
              <span className="text-[10px] text-slate-400">ENCRYPTED ATTACHMENT</span>
            </div>

            {/* Dual Evidence Thumbnail Preview */}
            <div className="grid grid-cols-2 gap-2">
              <div className="relative rounded-lg overflow-hidden border border-slate-700 h-28">
                <img
                  src="/cladding_defect.jpeg"
                  alt="Optical Cladding Defect Evidence"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 to-transparent flex items-end p-2">
                  <span className="text-[10px] text-red-300 font-bold">
                    RGB TEAR DETECTED
                  </span>
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden border border-slate-700 h-28">
                <img
                  src="/thermal_flir.jpeg"
                  alt="FLIR Thermal Moisture Evidence"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 to-transparent flex items-end p-2">
                  <span className="text-[10px] text-amber-300 font-bold">
                    FLIR ISOTHERM ΔT +8.4°C
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2 text-slate-300">
              <div className="flex justify-between border-b border-slate-800 pb-1">
                <span className="text-slate-500">Asset Tag:</span>
                <strong className="text-white">{formData.assetTag}</strong>
              </div>
              <div className="flex justify-between border-b border-slate-800 pb-1">
                <span className="text-slate-500">API 579-1 FFS Status:</span>
                <strong className="text-amber-400">RSF = 0.812 (Rerate Required)</strong>
              </div>
              <div className="flex justify-between border-b border-slate-800 pb-1">
                <span className="text-slate-500">GPS / Coordinates:</span>
                <span>LAT 51.9244° N, ELEV +14.2m</span>
              </div>
              <div className="flex justify-between border-b border-slate-800 pb-1">
                <span className="text-slate-500">Estimated Man-Hours:</span>
                <span>{formData.estimatedHours} Hours</span>
              </div>
            </div>

            <p className="text-slate-400 font-sans text-xs leading-relaxed pt-1">
              Automatic zero-touch dispatch attaches high-resolution optical imagery, radiometric temperature arrays, and calculated Level 1 remaining wall calculations to SAP notification header.
            </p>
          </div>
        </div>
      </div>

      {/* Dispatched Work Orders Table */}
      <div className="card bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="p-4 sm:p-5 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-slate-900">Synchronized Work Order Feed</h2>
            <p className="text-xs text-slate-500">Live enterprise queue across SAP PM and IBM Maximo</p>
          </div>

          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search work order or asset..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-60"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table text-xs sm:text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="py-3 px-4 font-mono font-bold text-slate-700">ORDER ID</th>
                <th className="py-3 px-4 font-mono font-bold text-slate-700">ASSET</th>
                <th className="py-3 px-4 font-bold text-slate-700">SYSTEM</th>
                <th className="py-3 px-4 font-bold text-slate-700">PRIORITY</th>
                <th className="py-3 px-4 font-bold text-slate-700">STATUS</th>
                <th className="py-3 px-4 font-mono font-bold text-slate-700">EXTERNAL REF</th>
                <th className="py-3 px-4 font-bold text-slate-700">WORK CENTER</th>
                <th className="py-3 px-4 font-bold text-right text-slate-700">ACTION</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-sans">
              {filteredOrders.map((wo) => (
                <tr key={wo.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-3 px-4 font-mono font-bold text-slate-900">{wo.id}</td>
                  <td className="py-3 px-4 font-mono font-semibold text-blue-700">{wo.assetTag}</td>
                  <td className="py-3 px-4 font-medium text-slate-700">{wo.cmmsSystem}</td>
                  <td className="py-3 px-4">{getPriorityBadge(wo.priority)}</td>
                  <td className="py-3 px-4">{getStatusBadge(wo.status)}</td>
                  <td className="py-3 px-4 font-mono text-slate-700">{wo.externalId}</td>
                  <td className="py-3 px-4 font-mono text-xs text-slate-500">{wo.workCenter} ({wo.estimatedHours}h)</td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => toast.success(`Viewing dossier for ${wo.externalId}`)}
                      className="btn btn-secondary text-xs py-1 px-2.5 shadow-xs"
                    >
                      Dossier →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
